import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "step", "stepIndicator", "nextBtn", "prevBtn",
    "confirmBtn", "payBtn", "skipPayBtn", "completionPanel",
    "confirmationSummary", "paymentForm", "paymentMethodSelect",
    "transferReceiptSection", "transferReceiptInput", "transferReceiptDisplay",
    "completionInvoiceSection", "completionPaymentSection", "completionPendingNoticeSection",
    "invoicePdfLink", "receiptPdfLink",
    "sendInvoiceBtn", "sendReceiptBtn",
    "errorAlert", "invoiceWarning",
    "cuitWarning",
    "duplicateModal", "duplicateDetails"
  ]

  static values = {
    currentStep: { type: Number, default: 1 },
    totalSteps: { type: Number, default: 6 },
    saleId: Number,
    invoiceId: Number,
    hasInvoice: { type: Boolean, default: false },
    createUrl: String,
    csrfToken: String
  }

  connect() {
    this.billingDataLoaded = false
    this.paymentSkipped = false
    this.showStep(this.currentStepValue)
    this.setupPatientSelectionListener()
  }

  async parseJsonResponse(response) {
    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) {
      if (response.status === 401 || response.redirected) {
        throw new Error('Tu sesión ha expirado. Por favor, recargá la página e intentá nuevamente.')
      }
      throw new Error('El servidor respondió con un formato inesperado. Recargá la página e intentá nuevamente.')
    }
    return response.json()
  }

  setupPatientSelectionListener() {
    const patientSearchEl = document.querySelector('[data-controller*="user-search"]')
    if (patientSearchEl) {
      patientSearchEl.addEventListener('user-search:selected', (event) => {
        const { userId, type, userCuit } = event.detail
        if (type === 'sales' && userId) {
          // Sync patient_id to the form's hidden field
          const wizardPatientField = document.querySelector('#wizard_sale_patient_id')
          if (wizardPatientField) wizardPatientField.value = userId

          // Check if patient has CUIT and show/hide warning
          if (this.hasCuitWarningTarget) {
            if (!userCuit) {
              this.cuitWarningTarget.style.cssText = 'display: flex !important'
            } else {
              this.cuitWarningTarget.style.cssText = 'display: none !important'
            }
          }

          // Trigger billing data loading on the sale-form controller
          const saleFormController = this.getSaleFormController()
          if (saleFormController) {
            saleFormController.loadBillingData(userId)
            this.billingDataLoaded = true
          }
        }
      })
    }
  }

  // Navigation
  nextStep() {
    if (!this.validateCurrentStep()) return

    if (this.currentStepValue < this.totalStepsValue) {
      // If moving to step 3, ensure billing data is loaded
      if (this.currentStepValue === 2) {
        this.ensureBillingDataLoaded()
      }
      // If moving from step 3 to step 4, build confirmation summary
      if (this.currentStepValue === 3) {
        this.buildConfirmationSummary()
      }
      this.currentStepValue++
      this.showStep(this.currentStepValue)
    }
  }

  ensureBillingDataLoaded() {
    if (this.billingDataLoaded) return

    const patientId = document.querySelector('#sale_patient_id')?.value
    if (!patientId) return

    const saleFormController = this.getSaleFormController()
    if (saleFormController) {
      saleFormController.loadBillingData(patientId)
      this.billingDataLoaded = true
    }
  }

  previousStep() {
    if (this.currentStepValue > 1) {
      this.currentStepValue--
      this.showStep(this.currentStepValue)
    }
  }

  goToStep(event) {
    const step = parseInt(event.currentTarget.dataset.step)
    if (step && step <= this.currentStepValue) {
      this.currentStepValue = step
      this.showStep(this.currentStepValue)
    }
  }

  showStep(step) {
    // Show/hide step panels
    this.stepTargets.forEach(panel => {
      const panelStep = parseInt(panel.dataset.step)
      panel.style.display = panelStep === step ? 'block' : 'none'
    })

    // Update stepper indicators
    this.stepIndicatorTargets.forEach(indicator => {
      const indicatorStep = parseInt(indicator.dataset.step)
      indicator.classList.remove('active', 'completed')
      if (indicatorStep === step) {
        indicator.classList.add('active')
      } else if (indicatorStep < step) {
        indicator.classList.add('completed')
      }
    })

    // Show/hide navigation buttons
    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.style.display = step > 1 && step < 5 ? 'inline-block' : 'none'
    }
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.style.display = step < 4 ? 'inline-block' : 'none'
    }
    if (this.hasConfirmBtnTarget) {
      this.confirmBtnTarget.style.display = step === 4 ? 'inline-block' : 'none'
    }
    if (this.hasPayBtnTarget) {
      this.payBtnTarget.style.display = step === 5 ? 'inline-block' : 'none'
    }
    if (this.hasSkipPayBtnTarget) {
      this.skipPayBtnTarget.style.display = step === 5 ? 'inline-block' : 'none'
    }
  }

  // Validation per step
  validateCurrentStep() {
    this.hideError()

    switch (this.currentStepValue) {
      case 1:
        return this.validatePatientStep()
      case 2:
        return this.validateProductsStep()
      case 3:
        return this.validateInvoiceStep()
      default:
        return true
    }
  }

  validatePatientStep() {
    const patientId = document.querySelector('#sale_patient_id')?.value
    if (!patientId) {
      this.showError('Debe seleccionar un paciente para continuar.')
      return false
    }
    return true
  }

  validateProductsStep() {
    const saleFormController = this.getSaleFormController()
    if (!saleFormController || !saleFormController.isValid()) {
      this.showError('Debe agregar al menos un producto con cantidad y precio.')
      return false
    }
    return true
  }

  validateInvoiceStep() {
    const saleFormController = this.getSaleFormController()
    if (!saleFormController) return true

    if (saleFormController.shouldInvoice()) {
      const cuit = saleFormController.getBillingCuit()
      const receptorType = saleFormController.getReceptorType()
      if (!cuit || !receptorType) {
        this.showError('Para facturar, debe seleccionar un CUIT y la condición IVA del receptor.')
        return false
      }
    }
    return true
  }

  // Confirmation step
  buildConfirmationSummary() {
    if (!this.hasConfirmationSummaryTarget) return

    const saleFormController = this.getSaleFormController()
    if (!saleFormController) return

    const patientInput = document.querySelector('[data-user-search-target="input"]')
    const patientName = patientInput ? patientInput.value : 'N/A'
    const items = saleFormController.getItems()
    const total = saleFormController.getGrandTotal()
    const willInvoice = saleFormController.shouldInvoice()

    let itemsHtml = items.map(item =>
      `<tr>
        <td>${this.escapeHtml(item.name)}</td>
        <td class="text-center">${item.quantity}</td>
        <td class="text-right">$${item.unit_price.toFixed(2)}</td>
        <td class="text-right">$${item.subtotal.toFixed(2)}</td>
      </tr>`
    ).join('')

    let invoiceHtml = ''
    if (willInvoice) {
      const cuit = saleFormController.getBillingCuit() || 'N/A'
      const receptorType = saleFormController.getReceptorType() || 'N/A'
      const receptorLabel = {
        'consumidor_final': 'Consumidor Final',
        'responsable_inscripto': 'Responsable Inscripto',
        'monotributista': 'Monotributista',
        'exento_iva': 'Exento IVA'
      }[receptorType] || receptorType

      invoiceHtml = `
        <div class="alert alert-info mt-3">
          <i class="fas fa-file-invoice mr-1"></i>
          <strong>Se generará factura</strong>
          <div class="mt-1">
            <small>CUIT: ${this.escapeHtml(cuit)} | Condición: ${this.escapeHtml(receptorLabel)}</small>
          </div>
        </div>
      `
    } else {
      invoiceHtml = `
        <div class="alert alert-secondary mt-3">
          <i class="fas fa-times-circle mr-1"></i> Sin facturación
        </div>
      `
    }

    const notesInput = document.querySelector('#sale_notes')
    const notes = notesInput?.value || ''
    const notesHtml = notes ? `<p class="mt-2"><strong>Notas:</strong> ${this.escapeHtml(notes)}</p>` : ''

    this.confirmationSummaryTarget.innerHTML = `
      <div class="card">
        <div class="card-body">
          <h6 class="card-title"><i class="fas fa-user mr-1"></i> Paciente</h6>
          <p class="mb-3">${this.escapeHtml(patientName)}</p>

          <h6><i class="fas fa-shopping-cart mr-1"></i> Productos</h6>
          <table class="table table-sm">
            <thead>
              <tr><th>Producto</th><th class="text-center">Cant.</th><th class="text-right">P. Unit.</th><th class="text-right">Subtotal</th></tr>
            </thead>
            <tbody>${itemsHtml}</tbody>
            <tfoot>
              <tr class="font-weight-bold">
                <td colspan="3" class="text-right">Total:</td>
                <td class="text-right">$${total.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>

          ${invoiceHtml}
          ${notesHtml}
        </div>
      </div>
    `
  }

  // Step 4: Confirm and create sale
  async confirmSale() {
    if (this._confirmingInProgress) return
    this._confirmingInProgress = true

    if (this.hasConfirmBtnTarget) {
      this.confirmBtnTarget.disabled = true
      this.confirmBtnTarget.innerHTML = '<i class="fas fa-spinner fa-spin mr-1"></i> Procesando...'
    }

    try {
      const saleFormController = this.getSaleFormController()
      if (!saleFormController) throw new Error('No se encontró el formulario de venta')

      // Sync patient_id from step 1 into the form
      const patientId = document.querySelector('#sale_patient_id')?.value
      const wizardPatientField = document.querySelector('#wizard_sale_patient_id')
      if (wizardPatientField && patientId) wizardPatientField.value = patientId

      // Prepare form data
      saleFormController.prepareFormForSubmit()
      saleFormController.calculateGrandTotal()

      const form = document.querySelector('[data-controller*="sale-form"]')?.closest('form')
      if (!form) throw new Error('Formulario no encontrado')

      const formData = new FormData(form)

      // Add invoice params if applicable
      if (saleFormController.shouldInvoice()) {
        formData.append('should_invoice', 'true')
        formData.append('invoice[billing_cuit]', saleFormController.getBillingCuit() || '')
        formData.append('invoice[receptor_type]', saleFormController.getReceptorType() || '')
      }

      // El operador ya vio el aviso de venta duplicada y confirmó crearla igual
      if (this._duplicateOverride) {
        formData.append('duplicate_override', 'true')
      }

      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfTokenValue
        },
        body: formData
      })

      const data = await this.parseJsonResponse(response)

      if (data.success) {
        this.saleIdValue = data.sale_id
        this.invoiceIdValue = data.invoice_id || 0
        this.hasInvoiceValue = !!data.invoice_id

        // Update view-sale link
        const viewSaleLink = document.querySelector('#view-sale-link')
        if (viewSaleLink) viewSaleLink.href = `/sales/${data.sale_id}`

        // La venta se creó. Si la facturación falló (p. ej. Colppy caído / su error
        // de sFPDF), avisamos sin bloquear: la venta existe y se puede facturar luego.
        // Avanzamos igual y NO reintentamos crearla, que era lo que duplicaba la venta.
        if (data.invoice_error) {
          this.showInvoiceWarning(data.invoice_error)
        } else {
          this.hideInvoiceWarning()
        }

        // Move to payment step
        this.currentStepValue = 5
        this.showStep(5)
      } else if (data.duplicate_warning) {
        this.showDuplicateWarning(data.existing_sale)
      } else {
        const errorMsg = data.errors ? data.errors.join(', ') : (data.error || 'Error desconocido')
        this.showError(`Error al crear la venta: ${errorMsg}`)
      }
    } catch (error) {
      console.error('Error confirming sale:', error)
      this.showError(`Error: ${error.message}`)
    } finally {
      this._confirmingInProgress = false
      this._duplicateOverride = false
      if (this.hasConfirmBtnTarget && !this.saleIdValue) {
        this.confirmBtnTarget.disabled = false
        this.confirmBtnTarget.innerHTML = '<i class="fas fa-check mr-1"></i> Confirmar Venta'
      }
    }
  }

  // Pop-up de venta duplicada: el servidor detectó otra venta igual hoy para
  // el mismo paciente y frenó la creación hasta que el operador confirme.
  showDuplicateWarning(existing) {
    if (!this.hasDuplicateModalTarget) {
      // Sin el modal en el DOM caemos al confirm nativo para no bloquear la venta
      if (window.confirm('Ya existe una venta igual hoy para este paciente. ¿Crear de todos modos?')) {
        this.confirmDuplicateSale()
      }
      return
    }

    if (this.hasDuplicateDetailsTarget) {
      const parts = []
      if (existing?.time) parts.push(`Creada a las ${existing.time}`)
      if (existing?.items) parts.push(existing.items)
      if (existing?.total != null) parts.push(`$${Number(existing.total).toLocaleString('es-AR')}`)
      parts.push(existing?.invoice ? `Factura ${existing.invoice}` : 'Sin factura')
      if (existing?.payment_status) parts.push(existing.payment_status)
      this.duplicateDetailsTarget.textContent = parts.join(' · ')
    }

    $(this.duplicateModalTarget).modal('show')
  }

  confirmDuplicateSale() {
    this._duplicateOverride = true
    if (this.hasDuplicateModalTarget) {
      $(this.duplicateModalTarget).modal('hide')
    }
    this.confirmSale()
  }

  // Step 5: Skip payment
  skipPayment() {
    this.paymentSkipped = true
    this.setupCompletionStep(null)
    this.currentStepValue = 6
    this.showStep(6)
  }

  // Step 5: Payment
  paymentMethodChanged(event) {
    const method = event.target.value
    if (this.hasTransferReceiptSectionTarget) {
      this.transferReceiptSectionTarget.style.display = method === 'wire' ? 'block' : 'none'
    }
  }

  updateReceiptFileName(event) {
    const fileInput = event.target
    if (this.hasTransferReceiptDisplayTarget) {
      if (fileInput.files && fileInput.files.length > 0) {
        this.transferReceiptDisplayTarget.value = fileInput.files[0].name
      } else {
        this.transferReceiptDisplayTarget.value = ''
      }
    }
  }

  async recordPayment() {
    if (!this.hasPaymentMethodSelectTarget) return

    const paymentMethod = this.paymentMethodSelectTarget.value
    if (!paymentMethod) {
      this.showError('Debe seleccionar un método de pago.')
      return
    }

    if (this.hasPayBtnTarget) {
      this.payBtnTarget.disabled = true
      this.payBtnTarget.innerHTML = '<i class="fas fa-spinner fa-spin mr-1"></i> Registrando pago...'
    }

    try {
      const formData = new FormData()
      formData.append('payment_method', paymentMethod)

      // Attach transfer receipt if present
      if (paymentMethod === 'wire' && this.hasTransferReceiptInputTarget) {
        const fileInput = this.transferReceiptInputTarget
        if (fileInput.files && fileInput.files.length > 0) {
          formData.append('transfer_receipt', fileInput.files[0])
        }
      }

      const response = await fetch(`/sales/${this.saleIdValue}/record_payment`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfTokenValue
        },
        body: formData
      })

      const data = await this.parseJsonResponse(response)

      if (data.success) {
        this.setupCompletionStep(data)
        this.currentStepValue = 6
        this.showStep(6)
      } else {
        this.showError(`Error al registrar el pago: ${data.error || 'Error desconocido'}`)
      }
    } catch (error) {
      console.error('Error recording payment:', error)
      this.showError(`Error: ${error.message}`)
    } finally {
      if (this.hasPayBtnTarget) {
        this.payBtnTarget.disabled = false
        this.payBtnTarget.innerHTML = '<i class="fas fa-credit-card mr-1"></i> Registrar Pago'
      }
    }
  }

  // Step 6: Completion
  setupCompletionStep(paymentData) {
    // Show/hide invoice section
    if (this.hasCompletionInvoiceSectionTarget) {
      this.completionInvoiceSectionTarget.style.display = this.hasInvoiceValue ? 'block' : 'none'
    }

    // Show/hide payment receipt section vs pending notice
    if (this.paymentSkipped) {
      if (this.hasCompletionPaymentSectionTarget) {
        this.completionPaymentSectionTarget.style.display = 'none'
      }
      if (this.hasCompletionPendingNoticeSectionTarget) {
        this.completionPendingNoticeSectionTarget.style.display = 'block'
      }
    } else {
      if (this.hasCompletionPaymentSectionTarget) {
        this.completionPaymentSectionTarget.style.display = 'block'
      }
      if (this.hasCompletionPendingNoticeSectionTarget) {
        this.completionPendingNoticeSectionTarget.style.display = 'none'
      }
    }

    // Set PDF download links
    if (this.hasInvoicePdfLinkTarget && this.hasInvoiceValue) {
      this.invoicePdfLinkTarget.href = `/sales/${this.saleIdValue}/invoice_pdf`
    }
    if (this.hasReceiptPdfLinkTarget && !this.paymentSkipped) {
      this.receiptPdfLinkTarget.href = `/sales/${this.saleIdValue}/payment_receipt_pdf`
    }
  }

  async sendInvoiceEmail() {
    if (!this.hasSendInvoiceBtnTarget) return
    this.sendInvoiceBtnTarget.disabled = true
    this.sendInvoiceBtnTarget.innerHTML = '<i class="fas fa-spinner fa-spin mr-1"></i> Enviando...'

    try {
      const response = await fetch(`/sales/${this.saleIdValue}/send_invoice_email`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfTokenValue
        }
      })
      const data = await this.parseJsonResponse(response)
      if (data.success) {
        this.sendInvoiceBtnTarget.innerHTML = '<i class="fas fa-check text-success mr-1"></i> Enviada'
      } else {
        this.sendInvoiceBtnTarget.innerHTML = '<i class="fas fa-times text-danger mr-1"></i> Error'
        this.showError(data.error || 'Error al enviar')
      }
    } catch (error) {
      this.sendInvoiceBtnTarget.innerHTML = '<i class="fas fa-times text-danger mr-1"></i> Error'
    }
  }

  async sendReceiptEmail() {
    if (!this.hasSendReceiptBtnTarget) return
    this.sendReceiptBtnTarget.disabled = true
    this.sendReceiptBtnTarget.innerHTML = '<i class="fas fa-spinner fa-spin mr-1"></i> Enviando...'

    try {
      const response = await fetch(`/sales/${this.saleIdValue}/send_receipt_email`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfTokenValue
        }
      })
      const data = await this.parseJsonResponse(response)
      if (data.success) {
        this.sendReceiptBtnTarget.innerHTML = '<i class="fas fa-check text-success mr-1"></i> Enviado'
      } else {
        this.sendReceiptBtnTarget.innerHTML = '<i class="fas fa-times text-danger mr-1"></i> Error'
        this.showError(data.error || 'Error al enviar')
      }
    } catch (error) {
      this.sendReceiptBtnTarget.innerHTML = '<i class="fas fa-times text-danger mr-1"></i> Error'
    }
  }

  // Helpers
  getSaleFormController() {
    const el = document.querySelector('[data-controller*="sale-form"]')
    if (!el) return null
    return this.application.getControllerForElementAndIdentifier(el, 'sale-form')
  }

  showError(message) {
    if (this.hasErrorAlertTarget) {
      this.errorAlertTarget.textContent = message
      this.errorAlertTarget.style.display = 'block'
      this.errorAlertTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  hideError() {
    if (this.hasErrorAlertTarget) {
      this.errorAlertTarget.style.display = 'none'
    }
  }

  showInvoiceWarning(message) {
    if (this.hasInvoiceWarningTarget) {
      this.invoiceWarningTarget.innerHTML =
        `<i class="fas fa-exclamation-triangle mr-2"></i>` +
        `<strong>La venta se creó, pero no se pudo generar la factura:</strong> ${this.escapeHtml(message)}` +
        `<br><small>Podés facturarla más tarde desde el detalle de la venta con el botón "Facturar".</small>`
      this.invoiceWarningTarget.style.display = 'block'
      this.invoiceWarningTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  hideInvoiceWarning() {
    if (this.hasInvoiceWarningTarget) {
      this.invoiceWarningTarget.style.display = 'none'
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
